import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { logout } from "../lib/auth";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { MobileTabBar } from "./layout/MobileTabBar";

interface LayoutProps {
  authenticated: boolean;
  children: ReactNode;
}

interface NavEntry {
  to: string;
  labelKey: string;
  defaultLabel?: string;
  icon: ReactNode;
  end?: boolean;
}

interface NavGroup {
  labelKey: string;
  defaultLabel: string;
  items: NavEntry[];
}

export function Layout({ authenticated, children }: LayoutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!authenticated) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ background: "var(--bg)", color: "var(--text)" }}
      >
        <header
          className="border-b"
          style={{ background: "var(--card)", borderColor: "var(--border)" }}
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <span className="font-semibold" style={{ color: "var(--accent)" }}>
              {t("app.title")}
            </span>
            <div className="flex items-center gap-3">
              <ThemeToggle compact />
              <LanguageSwitcher />
            </div>
          </div>
        </header>
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">{children}</main>
      </div>
    );
  }

  const groups: NavGroup[] = [
    {
      labelKey: "nav.group_assets",
      defaultLabel: "Vermögen",
      items: [
        { to: "/", labelKey: "nav.dashboard", icon: <IconDashboard />, end: true },
        { to: "/accounts", labelKey: "nav.accounts", icon: <IconAccounts /> },
        { to: "/networth", labelKey: "nav.networth", icon: <IconNetworth /> },
        { to: "/allocation", labelKey: "nav.allocation", icon: <IconAllocation /> },
        { to: "/investments", labelKey: "nav.investments", icon: <IconInvestments /> },
        { to: "/otterfolio", labelKey: "nav.otterfolio", icon: <IconOtterfolio /> },
        { to: "/realestate", labelKey: "nav.realestate", icon: <IconRealEstate /> },
      ],
    },
    {
      labelKey: "nav.group_cashflow",
      defaultLabel: "Cashflow",
      items: [
        { to: "/cashflow", labelKey: "nav.cashflow", icon: <IconCashflow /> },
        { to: "/budget", labelKey: "nav.budget", defaultLabel: "Budget", icon: <IconBudget /> },
        { to: "/insurance", labelKey: "nav.insurance", icon: <IconInsurance /> },
        { to: "/pension", labelKey: "nav.pension", icon: <IconPension /> },
      ],
    },
    {
      labelKey: "nav.group_reports",
      defaultLabel: "Reports & Admin",
      items: [
        { to: "/dividends", labelKey: "nav.dividends", icon: <IconDividends /> },
        { to: "/import", labelKey: "nav.import", defaultLabel: "Import", icon: <IconImport /> },
        { to: "/settings", labelKey: "nav.settings", icon: <IconSettings /> },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)", color: "var(--text)" }}>
      {/* Sidebar */}
      <aside
        className="w-60 shrink-0 border-r flex-col sticky top-0 h-screen hidden md:flex"
        style={{ background: "var(--card)", borderColor: "var(--border)" }}
      >
        <div
          className="px-5 py-4 flex items-center gap-2 border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
            style={{ background: "var(--accent)" }}
            aria-hidden="true"
          >
            F
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm leading-tight truncate">{t("app.title")}</div>
            <div className="text-xs truncate" style={{ color: "var(--muted)" }}>
              {t("app.subtitle")}
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {groups.map((group) => (
            <div key={group.labelKey}>
              <div
                className="text-[11px] font-semibold tracking-wider uppercase px-3 pt-3.5 pb-1.5"
                style={{ color: "var(--subtle)" }}
              >
                {t(group.labelKey, { defaultValue: group.defaultLabel })}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? "fv-nav-active" : "fv-nav-idle"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive
                      ? {
                          background: "var(--accent-soft)",
                          color: "var(--accent)",
                        }
                      : { color: "var(--muted)" }
                  }
                >
                  <span className="shrink-0" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="truncate">
                    {t(item.labelKey, { defaultValue: item.defaultLabel })}
                  </span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div
          className="px-3 py-3 border-t flex items-center justify-between text-xs gap-2"
          style={{ borderColor: "var(--border)", color: "var(--muted)" }}
        >
          <button
            type="button"
            onClick={handleLogout}
            className="hover:underline"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.logout")}
          </button>
          <div className="flex items-center gap-1">
            <ThemeToggle compact />
            <LanguageSwitcher />
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden pb-20 md:pb-0">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-4 md:py-8">{children}</div>
      </main>
      <MobileTabBar />

      {/* Hover-/Idle-Style for NavLink — applied via class fallback */}
      <style>{`
        .fv-nav-idle:hover { background: var(--row-hover); color: var(--text) !important; }
      `}</style>
    </div>
  );
}

/* ============ Icons ============ */
const IC = "w-[18px] h-[18px]";
const SVG = (children: ReactNode) => (
  <svg
    className={IC}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconDashboard = () => SVG(<path d="M3 12l9-9 9 9M5 10v10h14V10" />);
const IconAccounts = () =>
  SVG(
    <>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18M7 15h4" />
    </>,
  );
const IconNetworth = () => SVG(<path d="M3 17l6-6 4 4 8-8M14 7h7v7" />);
const IconAllocation = () =>
  SVG(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v9l6 4" />
    </>,
  );
const IconInvestments = () => SVG(<path d="M4 20V8M10 20V4M16 20v-8M22 20H2" />);
const IconOtterfolio = () =>
  SVG(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 10h.01M15 10h.01M8 14c1 1.5 2.5 2 4 2s3-.5 4-2" />
    </>,
  );
const IconRealEstate = () => SVG(<path d="M3 21V10l9-7 9 7v11M9 21v-7h6v7" />);
const IconCashflow = () =>
  SVG(<path d="M21 12c0 5-4 9-9 9s-9-4-9-9 4-9 9-9c2.5 0 4.8 1 6.5 2.7M21 3v6h-6" />);
const IconBudget = () =>
  SVG(
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 14h4" />
    </>,
  );
const IconInsurance = () => SVG(<path d="M12 22s8-4 8-12V5l-8-3-8 3v5c0 8 8 12 8 12z" />);
const IconPension = () =>
  SVG(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 0112 0v1" />
    </>,
  );
const IconDividends = () =>
  SVG(
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>,
  );
const IconImport = () => SVG(<path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />);
const IconSettings = () =>
  SVG(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
    </>,
  );
